import { UPGRADE_URL } from '../config/permissions';

// Structured payload returned when a zeroday user requests a ticker outside
// their plan — by portal's ZerodayTickerScopedMixin, and by QuantForge on
// /v1/gex-levels and /v1/ohlc. Surfaced inline in the widget body instead of
// the generic error overlay.
export interface TickerNotInPlanPayload {
  code: 'ticker_not_in_plan';
  message: string;
  allowed_tickers: string[];
  upgrade_url: string;
}

function isTickerNotInPlan(v: unknown): boolean {
  return (
    !!v &&
    typeof v === 'object' &&
    (v as { code?: unknown }).code === 'ticker_not_in_plan'
  );
}

function asStrings(v: unknown): string[] | null {
  return Array.isArray(v) && v.every((x) => typeof x === 'string')
    ? (v as string[])
    : null;
}

/**
 * Build the out-of-plan card from a failed request.
 *
 * **Any 403 counts.** A refusal on a ticker request means the plan does not
 * cover it, so the widget always offers an upgrade rather than requiring the
 * body to be shaped a particular way. Insisting on an exact payload used to
 * fail closed on presentation — an unrecognised refusal became a generic
 * error with no way to pay us, which is the worst outcome on a revenue
 * surface.
 *
 * Server fields are treated as ENRICHMENT: QuantForge's `message`,
 * `allowed_tickers` and `upgrade_url` are used when present, and sensible
 * defaults fill in when they are not. Both envelope shapes are read — portal
 * (DRF) puts the payload at the body's top level, QuantForge (FastAPI) nests
 * it under `detail`.
 */
export function tickerNotInPlanFrom(
  err: unknown,
  ticker: string
): TickerNotInPlanPayload | null {
  if (!err || typeof err !== 'object') return null;
  const res = (err as { response?: { status?: number; data?: unknown } }).response;
  if (!res) return null;

  const data = res.data;
  const detail = (data as { detail?: unknown } | undefined)?.detail;
  const body = (isTickerNotInPlan(detail) ? detail : data) as
    | Partial<TickerNotInPlanPayload>
    | undefined;

  // A 403 is the signal. A recognised code also counts, in case a backend
  // ever reports the refusal under a different status.
  if (res.status !== 403 && !isTickerNotInPlan(body) && !isTickerNotInPlan(detail)) {
    return null;
  }

  const symbol = (ticker || '').toUpperCase();
  return {
    code: 'ticker_not_in_plan',
    message:
      typeof body?.message === 'string' && body.message
        ? body.message
        : `${symbol || 'This ticker'} isn't included in your plan.`,
    allowed_tickers: asStrings(body?.allowed_tickers) ?? [],
    upgrade_url:
      typeof body?.upgrade_url === 'string' && body.upgrade_url
        ? body.upgrade_url
        : UPGRADE_URL,
  };
}
